/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState, useEffect } from "react";
import ConstraintCard from "./ConstraintCard";

/**
 * ScheduledTransitionBuilder — changes that happen because a time has passed.
 *
 * Props:
 *   value            — array of { name, when_field, set_field, set_value, where?,
 *                      when_table?, when_match_field? }
 *   availableColumns — array of { name, data_type } on this table
 *   allTables        — array of { name, fields: [{name}] } for reading the time
 *                      off a related row
 */
export default function ScheduledTransitionBuilder({
  value = [], onChange, availableColumns = [], allTables = [], onEditingChange,
}) {
  const [editing, setEditing] = useState(null);
  useEffect(() => { if (onEditingChange) onEditingChange(!!editing); }, [editing, onEditingChange]);

  const WHERE_BANNED = [[";", "a semicolon"], ["--", "a SQL comment"], ["/*", "a block comment"]];
  function whereError(w) {
    const t = (w || "").trim();
    if (!t) return "";
    for (const [tok, described] of WHERE_BANNED) {
      if (t.includes(tok)) return `Remove ${described} — this takes a single condition.`;
    }
    return "";
  }

  const d = editing && editing.data;
  const elsewhere = d && d.source === "other";
  const valid = d && d.name.trim() && d.when_field && d.set_field
    && String(d.set_value).trim() !== "" && !whereError(d.where)
    && (!elsewhere || (d.when_table && d.when_match_field));

  const saveHint = "Name it, choose the moment to wait for, and say what to change.";

  function blank() {
    return { name: "", source: "self", when_table: "", when_match_field: "", when_field: "",
             set_field: "", set_value: "", where: "" };
  }
  function startNew() { setEditing({ index: "new", data: blank() }); }
  function startEdit(idx) {
    const row = value[idx] || {};
    setEditing({ index: idx, data: { ...blank(), ...row, source: row.when_table ? "other" : "self" } });
  }
  function cancel() { setEditing(null); }
  function remove(idx) { onChange(value.filter((_, i) => i !== idx)); }
  function patch(p) { setEditing(e => ({ ...e, data: { ...e.data, ...p } })); }

  function save() {
    if (!valid) return;
    const row = { name: d.name.trim(), when_field: d.when_field,
                  set_field: d.set_field, set_value: d.set_value };
    if (elsewhere) { row.when_table = d.when_table; row.when_match_field = d.when_match_field; }
    if ((d.where || "").trim()) row.where = d.where.trim();
    if (editing.index === "new") onChange([...value, row]);
    else onChange(value.map((r, i) => i === editing.index ? row : r));
    setEditing(null);
  }

  const timeColumns = elsewhere
    ? ((allTables.find(t => t.name === d.when_table) || {}).fields || [])
    : availableColumns;
  const parentColumns = (allTables.find(t => t.name === (d && d.when_table)) || {}).fields || [];
  const sel = { fontSize: 13 };
  const lbl = { fontSize: 12, color: "#555" };

  const editorBody = editing && (
    <>
      <div className="mb-2">
        <label style={lbl}>Name</label>
        <input className="form-control" style={sel} placeholder="e.g. open_registration"
          value={d.name} onChange={e => patch({ name: e.target.value })} />
      </div>

      <div className="mb-2">
        <label style={lbl}>The moment to wait for</label>
        <div className="d-flex gap-3 mb-2" style={{ fontSize: 13 }}>
          <label style={{ cursor: "pointer" }}>
            <input type="radio" checked={!elsewhere} onChange={() => patch({ source: "self", when_field: "" })} />{" "}
            A date on this record
          </label>
          <label style={{ cursor: "pointer" }}>
            <input type="radio" checked={elsewhere} onChange={() => patch({ source: "other", when_field: "" })} />{" "}
            A date on a related record
          </label>
        </div>

        {elsewhere && (
          <div className="d-flex gap-2 flex-wrap mb-2">
            <div style={{ minWidth: 210 }}>
              <label style={lbl}>Related table</label>
              <select className="form-select" style={sel} value={d.when_table}
                onChange={e => patch({ when_table: e.target.value, when_match_field: "", when_field: "" })}>
                <option value="">— choose —</option>
                {allTables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div style={{ minWidth: 190 }}>
              <label style={lbl}>Linked by</label>
              <select className="form-select" style={sel} value={d.when_match_field}
                onChange={e => patch({ when_match_field: e.target.value })} disabled={!d.when_table}>
                <option value="">— choose —</option>
                {parentColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
              </select>
            </div>
          </div>
        )}

        <select className="form-select" style={{ ...sel, maxWidth: 320 }} value={d.when_field}
          onChange={e => patch({ when_field: e.target.value })}>
          <option value="">— choose the date field —</option>
          {timeColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
        </select>
      </div>

      <div className="mb-2 d-flex gap-2 flex-wrap">
        <div style={{ minWidth: 200 }}>
          <label style={lbl}>Then set</label>
          <select className="form-select" style={sel} value={d.set_field}
            onChange={e => patch({ set_field: e.target.value })}>
            <option value="">— choose a field —</option>
            {availableColumns.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
        <div style={{ minWidth: 200 }}>
          <label style={lbl}>To</label>
          <input className="form-control" style={sel} placeholder="e.g. REGISTRATION_OPEN"
            value={d.set_value} onChange={e => patch({ set_value: e.target.value })} />
        </div>
      </div>

      <div>
        <label style={lbl}>Only for records where <span style={{ color: "#888" }}>(optional)</span></label>
        <input className="form-control" style={{ ...sel, fontFamily: "monospace" }}
          placeholder="e.g. status = 'PUBLISHED'"
          value={d.where || ""} onChange={e => patch({ where: e.target.value })} />
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
          <i className="bi bi-info-circle" /> Use this to protect records that should be left
          alone — a cancelled event should not quietly mark itself finished.
        </div>
        {whereError(d.where) && (
          <div style={{ fontSize: 12, color: "#b42318", marginTop: 4 }}>
            <i className="bi bi-exclamation-triangle" /> {whereError(d.where)}
          </div>
        )}
      </div>
    </>
  );

  function summaryOf(row) {
    const when = row.when_table ? `${row.when_table}.${row.when_field}` : row.when_field;
    return `when ${when} passes → ${row.set_field} = ${row.set_value}`
      + (row.where ? `, for ${row.where}` : "");
  }

  return (
    <div>
      {value.map((row, idx) => (
        editing && editing.index === idx ? (
          <ConstraintCard key={idx} mode="edit" title="Edit Scheduled Change"
            onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
            {editorBody}
          </ConstraintCard>
        ) : (
          <ConstraintCard key={idx} mode="summary" title={row.name || "(unnamed)"}
            canEdit={!editing}
            onEdit={() => startEdit(idx)} onRemove={() => remove(idx)}
            summary={<code style={{ fontSize: 12, color: "#555" }}>{summaryOf(row)}</code>} />
        )
      ))}

      {editing && editing.index === "new" && (
        <ConstraintCard mode="edit" title="New Scheduled Change"
          onCancel={cancel} onSave={save} saveDisabled={!valid} saveHint={saveHint}>
          {editorBody}
        </ConstraintCard>
      )}

      {!editing && (
        <button type="button" onClick={startNew}
          style={{ fontSize: 13, color: "#0d6efd", background: "none", border: "1px solid #0d6efd",
                   borderRadius: 6, padding: "4px 14px", cursor: "pointer", marginTop: 4 }}>
          <i className="bi bi-plus-lg" /> Add Scheduled Change
        </button>
      )}
    </div>
  );
}
